import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "../ui/Button.js";
import { Menu, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/stock_images/logo.png";

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Customers", href: "/" },
    { name: "Vendors", href: "/vendors" },
    { name: "Investors", href: "/investors" },
    { name: "Contact", href: "/contact" },
  ];
  const getPageConfig = () => {
    if (location === "/vendors") {
      return { id: "join-vendor-submit", label: "Join as Vendor" };
    }
    if (location === "/investors") {
      return { id: "invest-form", label: "Join as Investor" };
    }
    if (location === "/contact") {
      return { id: "contact-form", label: "Send Message" };
    }
    return { id: "join-waitlist-submit", label: "Join Waitlist" };
  };

  const pageConfig = getPageConfig();

  const handleAction = () => {
    setIsOpen(false);
    if (!pageConfig) return;

    const element = document.getElementById(pageConfig.id);
    if (element) {
      // Always scroll to element with offset to clear the fixed navbar
      const NAV_OFFSET = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top: elementTop, behavior: "smooth" });

      // Highlight the element briefly
      setTimeout(() => {
        element.classList.add("ring-4", "ring-primary", "ring-offset-4", "scale-110", "shadow-[0_0_30px_rgba(var(--primary),0.6)]", "transition-all", "duration-300");
        setTimeout(() => {
          element.classList.remove("ring-4", "ring-primary", "ring-offset-4", "scale-110", "shadow-[0_0_30px_rgba(var(--primary),0.6)]");
        }, 800);
      }, 400);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-background/70 backdrop-blur-2xl border-b border-border/40 shadow-soft py-3" : "bg-transparent py-5"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/">
            <div className="flex items-center gap-3 cursor-pointer group">
              <motion.div 
                whileHover={{ rotate: [-5, 5, 0], scale: 1.1 }}
                className="w-12 h-12 flex items-center justify-center bg-white rounded-2xl shadow-soft p-1 overflow-hidden"
              >
                <img
                  src={logo}
                  alt="Quro Logo"
                  className="w-full h-full object-contain"
                />
              </motion.div>
              <span className="font-display font-black text-2xl tracking-tighter group-hover:text-primary transition-colors">Quro</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10 ml-auto mr-12">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className={`group relative text-sm font-bold tracking-wide uppercase transition-all duration-300 hover:text-primary ${location === link.href ? "text-primary" : "text-muted-foreground/80"
                  }`}
              >
                {link.name}
                <motion.span 
                  initial={false}
                  animate={{ width: location === link.href ? "100%" : "0%" }}
                  className="absolute -bottom-1 left-0 h-0.5 bg-primary rounded-full transition-all duration-300"
                />
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {pageConfig && (
              <Button
                size="lg"
                className="h-12 px-6 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-glow shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:scale-95 transition-all"
                onClick={handleAction}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {pageConfig.label}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground w-12 h-12 rounded-2xl hover:bg-primary/5 active:scale-90 transition-all"
            >
              {isOpen ? <X className="w-8 h-8 font-bold" /> : <Menu className="w-8 h-8 font-bold" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden glass-morphism mx-4 my-4 rounded-[2rem] overflow-hidden"
          >
            <div className="px-6 pt-6 pb-8 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-5 py-4 rounded-2xl text-lg font-bold transition-all ${
                    location === link.href 
                      ? "bg-primary text-white shadow-glow shadow-primary/20" 
                      : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-6 flex flex-col gap-4">
                {pageConfig && (
                  <Button 
                    className="w-full h-14 rounded-2xl text-lg font-bold shadow-xl active:scale-95 transition-all" 
                    onClick={handleAction}
                  >
                    <Sparkles className="w-5 h-5 mr-3" />
                    {pageConfig.label}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
